/**
 * Shapes a team's daily throughput series for its two small charts
 * (shipcast-o7j.6): the history day by day, and how often each daily count
 * occurred.
 *
 * The series is what the simulation samples — one value per calendar day,
 * oldest first. A tracker-fed series knows its last day (`lastDay`), which
 * dates every bar; a hand-typed one does not, so its days go unlabelled.
 */

export interface DayBar {
  /** Stable key: the day's position, oldest first. */
  index: number;
  /** Items delivered that day. */
  value: number;
  /** The day (`YYYY-MM-DD`), when the series is dated. */
  date: string | null;
  /** Short month name on the first day of each month shown, when dated. */
  monthLabel: string | null;
}

export interface DayCountBar {
  /** Items delivered in one day. */
  value: number;
  /** Days that delivered exactly `value` items. */
  days: number;
}

const DAY_MS = 86_400_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

function dayOffset(isoDate: string, days: number): Date {
  return new Date(Date.parse(`${isoDate}T00:00:00Z`) + days * DAY_MS);
}

/**
 * One bar per day, dated back from the series' last day when it is known.
 * The first day shown and every 1st of a month carry the month's name.
 */
export function dailyBars(values: readonly number[], lastDay: string | null): DayBar[] {
  const n = values.length;
  let previousMonth: number | null = null;
  return values.map((value, index) => {
    const day = lastDay ? dayOffset(lastDay, index - (n - 1)) : null;
    const month = day ? day.getUTCMonth() : null;
    const monthLabel = month !== null && month !== previousMonth ? MONTHS[month]! : null;
    previousMonth = month;
    return { index, value, date: day ? day.toISOString().slice(0, 10) : null, monthLabel };
  });
}

/**
 * How many days delivered each count, from 0 to the largest — gaps included,
 * so the shape reads true. Fractional hand-typed values round to the nearest
 * whole item.
 */
export function dayCountBars(values: readonly number[]): DayCountBar[] {
  if (values.length === 0) return [];
  const counts = new Map<number, number>();
  let max = 0;
  for (const v of values) {
    const value = Math.round(v);
    counts.set(value, (counts.get(value) ?? 0) + 1);
    max = Math.max(max, value);
  }
  return Array.from({ length: max + 1 }, (_, value) => ({ value, days: counts.get(value) ?? 0 }));
}

/** The share of days that delivered nothing, 0–1; 0 for an empty series. */
export function zeroDayShare(values: readonly number[]): number {
  if (values.length === 0) return 0;
  return values.filter((v) => Math.round(v) === 0).length / values.length;
}
